<script>
import AutomationActionTeamMessageInput from './AutomationActionTeamMessageInput.vue';
import AutomationActionFileInput from './AutomationFileInput.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

// Zimobi imports
import AutomationCreateDeal from './atendeTotal/AutomationCreateDeal.vue';
import AutomationCreateDealWithVariables from './atendeTotal/AutomationCreateDealWithVariables.vue';
import AutomationAttachLabelToDeal from './atendeTotal/AutomationAttachLabelToDeal.vue';
import AutomationSendWhatsappTemplate from './atendeTotal/AutomationSendWhatsappTemplate.vue';
import AutomationUpdateCustomAttribute from './atendeTotal/AutomationUpdateCustomAttribute.vue';
import AutomationActionPortalLeadDistribution from './atendeTotal/AutomationActionPortalLeadDistribution.vue';
import { defineAsyncComponent } from 'vue';

const AutomationActionIfElse = defineAsyncComponent(
  () => import('custom/dashboard/components/widgets/AutomationActionIfElse.vue')
);
// Zimobi imports

export default {
  components: {
    AutomationActionTeamMessageInput,
    AutomationActionFileInput,
    WootMessageEditor,
    NextButton,
    SingleSelect,
    MultiSelect,
    NextInput,
    AutomationCreateDeal,
    AutomationCreateDealWithVariables,
    AutomationAttachLabelToDeal,
    AutomationSendWhatsappTemplate,
    AutomationUpdateCustomAttribute,
    AutomationActionPortalLeadDistribution,
    AutomationActionIfElse,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => null,
    },
    actionTypes: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    errorMessage: {
      type: String,
      default: '',
    },
    showActionInput: {
      type: Boolean,
      default: true,
    },
    initialFileName: {
      type: String,
      default: '',
    },
    isMacro: {
      type: Boolean,
      default: false,
    },
    dropdownMaxHeight: {
      type: String,
      default: 'max-h-80',
    },
    // Zimobi: the if/else action builds its own conditions for the rule's event.
    automationEventName: {
      type: String,
      default: '',
    },
    automationTypes: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['update:modelValue', 'input', 'removeAction', 'resetAction'],
  computed: {
    action_name: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_name;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_name: value });
        this.$emit('input', { ...payload, action_name: value });
      },
    },
    action_params: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_params;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_params: value });
        this.$emit('input', { ...payload, action_params: value });
      },
    },
    inputType() {
      // Zimobi: fork actions without an input declare no inputType.
      const actionType = this.actionTypes.find(
        action => action.key === this.action_name
      );
      return actionType ? actionType.inputType : null;
    },
    actionNameAsSelectModel: {
      get() {
        if (!this.action_name) return null;
        const found = this.actionTypes.find(a => a.key === this.action_name);
        return found ? { id: found.key, name: found.label } : null;
      },
      set(value) {
        this.action_name = value?.id || value;
      },
    },
    actionTypesAsOptions() {
      return this.actionTypes.map(a => ({ id: a.key, name: a.label }));
    },
    isVerticalLayout() {
      return ['team_message', 'textarea'].includes(this.inputType);
    },
    castMessageVmodel: {
      get() {
        if (!this.action_params) return null;
        if (Array.isArray(this.action_params)) {
          return this.action_params[0];
        }
        return this.action_params;
      },
      set(value) {
        this.action_params = value;
      },
    },
    isCreateOpportunityInPiperun() {
      return this.action_name === 'create_opportunity_in_piperun';
    },
    createDealAutomationDealNameInputPlaceholder() {
      return this.isCreateOpportunityInPiperun
        ? 'Nome da oportunidade'
        : undefined;
    },
  },
  methods: {
    removeAction() {
      this.$emit('removeAction');
    },
    resetAction() {
      this.$emit('resetAction');
    },
    onActionNameChange(value) {
      this.actionNameAsSelectModel = value;
      this.resetAction();
    },
  },
};
</script>

<template>
  <li class="list-none py-2 first:pt-0 last:pb-0">
    <div
      class="flex flex-col gap-2"
      :class="{ 'animate-wiggle': errorMessage }"
    >
      <div class="flex items-center gap-2">
        <SingleSelect
          :model-value="actionNameAsSelectModel"
          :options="actionTypesAsOptions"
          :dropdown-max-height="dropdownMaxHeight"
          disable-deselect
          class="flex-shrink-0"
          @update:model-value="onActionNameChange"
        />
        <template v-if="showActionInput && !isVerticalLayout">
          <SingleSelect
            v-if="inputType === 'search_select'"
            v-model="action_params"
            :options="dropdownValues"
            :dropdown-max-height="dropdownMaxHeight"
          />
          <MultiSelect
            v-else-if="inputType === 'multi_select'"
            v-model="action_params"
            :options="dropdownValues"
            :dropdown-max-height="dropdownMaxHeight"
          />
          <NextInput
            v-else-if="inputType === 'email'"
            v-model="action_params"
            type="email"
            size="sm"
            :placeholder="$t('AUTOMATION.ACTION.EMAIL_INPUT_PLACEHOLDER')"
          />
          <NextInput
            v-else-if="inputType === 'url'"
            v-model="action_params"
            type="url"
            size="sm"
            :placeholder="$t('AUTOMATION.ACTION.URL_INPUT_PLACEHOLDER')"
          />
          <NextInput
            v-else-if="inputType === 'text'"
            v-model="action_params"
            type="text"
            size="sm"
            :placeholder="$t('AUTOMATION.ACTION.TEXT_INPUT_PLACEHOLDER')"
          />
          <AutomationActionFileInput
            v-else-if="inputType === 'attachment'"
            v-model="action_params"
            :initial-file-name="initialFileName"
          />
        </template>
        <NextButton
          v-if="!isMacro"
          sm
          solid
          slate
          icon="i-lucide-trash"
          class="flex-shrink-0"
          @click="removeAction"
        />
      </div>
      <AutomationActionTeamMessageInput
        v-if="inputType === 'team_message'"
        v-model="action_params"
        :teams="dropdownValues"
        :dropdown-max-height="dropdownMaxHeight"
      />
      <!--
        Zimobi: `add_private_note` arrived with Chatwoot 4.4.0 declaring `inputType: 'textarea'`,
        and the fork's deal actions declare it too, so the editor is limited to the two message
        actions. Both end up in Messages::MessageBuilder, and Message runs the content through
        Liquid on create (`Liquidable#process_liquid_in_content`), so the variable picker is
        honoured for both.
      -->
      <WootMessageEditor
        v-if="
          inputType === 'textarea' &&
          ['send_message', 'add_private_note'].includes(action_name)
        "
        v-model="castMessageVmodel"
        rows="4"
        enable-variables
        :placeholder="$t('AUTOMATION.ACTION.TEAM_MESSAGE_INPUT_PLACEHOLDER')"
        class="[&_.ProseMirror-menubar]:hidden px-3 py-1 bg-n-alpha-1 rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong"
      />
      <!-- Zimobi: the fork's integration and if/else actions. -->
      <AutomationCreateDeal
        v-if="
          inputType === 'textarea' &&
          [
            'create_deal_in_vista_if_customer_exists',
            'create_opportunity_in_piperun',
            'create_deal_in_properfy',
          ].includes(action_name)
        "
        v-model="action_params"
        :deal-name-input-placeholder="
          createDealAutomationDealNameInputPlaceholder
        "
      />
      <AutomationCreateDealWithVariables
        v-if="inputType === 'textarea' && action_name === 'create_deal'"
        v-model="action_params"
      />
      <AutomationAttachLabelToDeal
        v-if="
          inputType === 'textarea' &&
          action_name === 'attach_label_to_deal_in_pipefy'
        "
        v-model="action_params"
      />
      <AutomationUpdateCustomAttribute
        v-if="
          inputType === 'textarea' && action_name === 'update_custom_attribute'
        "
        v-model="action_params"
      />
      <AutomationSendWhatsappTemplate
        v-if="
          inputType === null && action_name === 'send_whatsapp_template_message'
        "
        v-model="action_params"
      />
      <AutomationActionPortalLeadDistribution
        v-if="inputType === null && action_name === 'portal_lead_distribution'"
        v-model="action_params"
      />
      <AutomationActionIfElse
        v-if="inputType === null && action_name === 'if_else'"
        v-model="action_params"
        :automation-event-name="automationEventName"
        :automation-action-types="actionTypes"
        :automation-types="automationTypes"
      />
    </div>
    <span v-if="errorMessage" class="text-sm text-n-ruby-11">
      {{ errorMessage }}
    </span>
  </li>
</template>
